import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

import { insightApi } from '../api/InsightApi'
import type { InsightResult } from '../models/insight.types'
import type { InsightListParams } from '../models/insightApi.types'
import type {
  InsightEmptyKind,
  InsightFilteringMode,
  InsightFilters,
  InsightPlatformOption,
} from '../models/insightDisplay.types'
import { filterInsights, hasActiveInsightFilters } from '../service/filterInsights'
import { deriveInsightPlatformOptions } from '../service/insightCenter'
import type { InsightOutcomeGroup } from '../service/insightPresentation'
import { useInsightStore, type InsightStore } from '../stores/insightStore'

import { useAccount, useWorkspace } from '@/features/account'
import { ApiError, type Paginated } from '@/shared/http'

export type InsightsState = 'loading' | 'empty' | 'error' | 'ready'

interface InsightsApiPort {
  list: (
    accountId: number,
    workspaceId: number,
    params?: InsightListParams,
    signal?: AbortSignal,
  ) => Promise<Paginated<InsightResult>>
}

export interface UseInsightsOptions {
  accountId?: MaybeRefOrGetter<number | undefined>
  workspaceId?: MaybeRefOrGetter<string | number | undefined>
  api?: InsightsApiPort
  store?: InsightStore
  limit?: number
  filteringMode?: InsightFilteringMode
}

export interface UseInsights {
  list: ComputedRef<readonly InsightResult[]>
  grouped: ComputedRef<readonly InsightOutcomeGroup[]>
  count: ComputedRef<number>
  platforms: ComputedRef<readonly InsightPlatformOption[]>
  filters: Ref<InsightFilters>
  state: ComputedRef<InsightsState>
  emptyKind: ComputedRef<InsightEmptyKind>
  error: ComputedRef<ApiError | null>
  reload: () => Promise<void>
}

export function useInsights(options: UseInsightsOptions = {}): UseInsights {
  const account = options.accountId ? null : useAccount()
  const workspace = options.workspaceId ? null : useWorkspace()
  const api = options.api ?? insightApi
  const store = options.store ?? useInsightStore()
  const filters = ref<InsightFilters>({ outcome: null, severity: null, platform: null, q: '' })
  const platforms = ref<InsightPlatformOption[]>([])
  const state = ref<InsightsState>('loading')
  const error = ref<ApiError | null>(null)
  let generation = 0
  let controller: AbortController | null = null

  const context = computed(() => ({
    accountId: numericId(options.accountId ? toValue(options.accountId) : account?.accountId.value),
    workspaceId: numericId(
      options.workspaceId ? toValue(options.workspaceId) : workspace?.workspaceId.value,
    ),
  }))

  async function reload(): Promise<void> {
    const request = ++generation
    controller?.abort()
    const { accountId, workspaceId } = context.value
    if (accountId === undefined || workspaceId === undefined) {
      store.resetState()
      error.value = null
      state.value = 'empty'
      return
    }

    store.ensureWorkspace(workspaceId)
    const activeController = new AbortController()
    controller = activeController
    state.value = 'loading'
    error.value = null
    const requestContext = `${accountId}:${workspaceId}`

    try {
      const filteringMode = options.filteringMode ?? 'client'
      const serverFilters = filteringMode === 'server'
      const page = await api.list(
        accountId,
        workspaceId,
        {
          ...(options.limit === undefined ? {} : { limit: options.limit, offset: 0 }),
          ...(serverFilters && filters.value.outcome
            ? { outcome_category: filters.value.outcome }
            : {}),
          ...(serverFilters && filters.value.severity ? { severity: filters.value.severity } : {}),
          ...(serverFilters && filters.value.platform ? { platform: filters.value.platform } : {}),
          ...(serverFilters ? { q: filters.value.q } : {}),
        },
        activeController.signal,
      )
      if (!ownsRequest(request, requestContext, activeController.signal)) return
      const nextPlatforms = deriveInsightPlatformOptions(page.results)
      if (nextPlatforms.length > 0 || !hasActiveInsightFilters(filters.value)) {
        platforms.value = nextPlatforms
      }
      const results = serverFilters ? page.results : filterInsights(page.results, filters.value)
      const count = serverFilters ? page.count : results.length
      if (!store.setList(workspaceId, results, count)) return
      state.value = results.length > 0 ? 'ready' : 'empty'
    } catch (cause) {
      if (request !== generation || activeController.signal.aborted) return
      const normalized = toApiError(cause)
      if (normalized.kind === 'canceled') return
      error.value = normalized
      state.value = 'error'
    }
  }

  function ownsRequest(request: number, key: string, signal: AbortSignal): boolean {
    const current = context.value
    return (
      request === generation &&
      !signal.aborted &&
      key === `${current.accountId ?? ''}:${current.workspaceId ?? ''}`
    )
  }

  watch(
    [
      context,
      () => filters.value.outcome,
      () => filters.value.severity,
      () => filters.value.platform,
      () => filters.value.q,
    ],
    () => void reload(),
    { immediate: true },
  )
  onScopeDispose(() => {
    generation += 1
    controller?.abort()
  })

  return {
    list: computed(() => store.list),
    grouped: computed(() => store.byOutcome),
    count: computed(() => store.count),
    platforms: computed(() => platforms.value),
    filters,
    state: computed(() => state.value),
    emptyKind: computed(() => (hasActiveInsightFilters(filters.value) ? 'noFilterMatch' : 'none')),
    error: computed(() => error.value),
    reload,
  }
}

function numericId(value: string | number | undefined): number | undefined {
  if (value === undefined || (typeof value === 'string' && value.trim() === '')) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function toApiError(cause: unknown): ApiError {
  return ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
}
